import {ellipsis} from 'polished';
import React, {FC} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {Dropdown} from '../../../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {DropdownList} from '../../../../../../libs/shared/ui/src/components/dropdowns/dropdownList';
import {SimpleDropdownItem} from '../../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown/simpleDropdownItem';
import {useLinkNavigation} from '../../../../../../libs/shared/ui/src/components/navigation/useLinkNavigation';
import {
  PopoverCoordinator,
  PopoverTransferProps,
} from '../../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {TooltipCoordinator} from '../../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {TooltipOverflowCondition} from '../../../../../../libs/shared/ui/src/components/tooltips/tooltipOverflowCondition';
import {VisualSizesEnum} from '../../../../../../libs/shared/ui/src/styles/commonStyles';
import {ApplicationModel, ApplicationRequestModel} from '../../../../../models/api/applicationModel';
import {ApplicationIcon} from '../../../../common/applications/applicationIcon';
import {
  StyledActivityRulePillLearnMoreDiv,
  StyledActivityRulePillLinkIcon,
} from '../activityRulePill/activityRulePillHelpers';
import {StyledPillDiv, StyledPopoverCoordinatorWrapperDiv} from '../basic/activityThirdPartyPillStyleHelpers';

const connectorsLearnMoreLink = 'https://help.front.com/en/articles/2002';

/*
 * Props.
 */

interface ActivityApplicationRequestPillProps {
  applicationRequest: ApplicationRequestModel;
  application: ApplicationModel;
}

/*
 * Style.
 */

const StyledApplicationIconWrapper = styled.div`
  width: 18px;
  height: 18px;
  display: inline-block;
  margin: 0 -1px -6px 0;
  padding-right: 6px;
`;

const StyledApplicationRequestNameDiv = styled.div`
  align-content: center;
  ${ellipsis('182px')};
`;

const StyledPillContentsDiv = styled.div`
  display: flex;
`;

/*
 * Component.
 */

export const ActivityApplicationRequestPill: FC<ActivityApplicationRequestPillProps> = (props) => {
  const {applicationRequest, application} = props;

  const {navigateToLink} = useLinkNavigation();

  const onLearnMoreClick = () => {
    navigateToLink(connectorsLearnMoreLink);
  };

  return (
    <StyledPopoverCoordinatorWrapperDiv>
      <PopoverCoordinator
        renderButton={(isOpen) => renderApplicationRequestButton(isOpen, applicationRequest, application.id)}
        renderModal={(popoverProps) => renderDropdown(popoverProps, onLearnMoreClick)}
      />
    </StyledPopoverCoordinatorWrapperDiv>
  );
};

/*
 * Helpers.
 */

function renderApplicationRequestButton(
  isOpen: boolean,
  applicationRequest: ApplicationRequestModel,
  applicationId: number,
) {
  const hasHover = isOpen && Boolean(applicationRequest);

  return (
    <StyledPillDiv $hasHover={hasHover} shouldRemoveLeftMargin>
      <TooltipCoordinator render={() => applicationRequest.name}>
        <TooltipOverflowCondition>
          <StyledPillContentsDiv>
            <StyledApplicationIconWrapper>
              <ApplicationIcon applicationId={applicationId} size={VisualSizesEnum.SMALL} />
            </StyledApplicationIconWrapper>
            <StyledApplicationRequestNameDiv>{applicationRequest.name}</StyledApplicationRequestNameDiv>
          </StyledPillContentsDiv>
        </TooltipOverflowCondition>
      </TooltipCoordinator>
    </StyledPillDiv>
  );
}

function renderDropdown(popoverProps: PopoverTransferProps, onLearnMoreClick: () => void) {
  return (
    <Dropdown {...popoverProps} maxWidth={300}>
      <DropdownList {...popoverProps}>
        <SimpleDropdownItem onSelect={onLearnMoreClick}>
          <StyledActivityRulePillLearnMoreDiv>
            <Trans id="activity-application-request-pill-help">
              Learn more about application requests{' '}
              <StyledActivityRulePillLinkIcon name="externalLinkSmall" />
            </Trans>
          </StyledActivityRulePillLearnMoreDiv>
        </SimpleDropdownItem>
      </DropdownList>
    </Dropdown>
  );
}
